import { useState, useEffect, useMemo, useCallback } from 'react'
import { useStores, useThemeColors } from 'hooks'
import { RulesListProps } from '../rules-list/types'
import {
	GButton,
	GMultiSelect,
	GLoading,
	GDateRange,
	GTable,
	GEmpty,
	GChartTooltip,
	GInput,
	GTooltip
} from 'components/basic-blocks'
import { BarChart } from 'components/charts/bar-chart/bar-chart'
import { RuleStatus } from 'api-models'
import moment from 'moment'
import { SearchSm as FiSearch, FilterLines as FiFilter } from 'components/icons'
import { HelpCircle as AiOutlineQuestionCircle } from 'components/icons'
import { RuleForm } from '../rule-form'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { useQuery } from 'react-query'
import { Rules } from 'services/user/rules'
import { ScheduleReportsDialog } from '../../../../../reports/triggers/runReports'
import { capitalizeFirstLetter } from '../../../../../../../../utils/text'
import { Calendar as FiCalendar } from 'components/icons'
import { StatusOption, statusOptions } from './types'
import { useSearchParams } from 'react-router-dom'
import {
	processRulesAndExecutions,
	handleTableSort,
	createHandleRuleToggle,
	createAxisBottomFormat,
	getRuleMetricLabel
} from './utils'
import { useTableColumns } from './column'
import { mapFetchedRules } from '../rules-list/utils'
import { RuleFormType } from '../rule-form/types'
import { RuleAnalysisMetric } from './types'
import { Dropdown } from '@clickguard/design-system'

const themeColorDefinitions = {
	navy: { variable: '--chart-navy-series', fallback: '#111553' },
	brand: { variable: '--color-brand', fallback: '#554BFD' },
	muted: { variable: '--t-radio-gray', fallback: '#D9D8EA' },
	yellow: { variable: '--chart-rule-yellow', fallback: '#EEFF9D' },
	gray: { variable: '--t-gray-checkout', fallback: '#616064' }
} as const

export const RulesAnalyze = ({
	level,
	targetId,
	targetName,
	platform,
	activeTab
}: RulesListProps) => {
	const themeColors = useThemeColors(themeColorDefinitions)
	const { assetStore } = useStores()
	const { asset, demoMode } = assetStore
	const trackPostHogEvent = useTrackPostHogEvent()
	const [searchParams] = useSearchParams()

	const [selectedStatuses, setSelectedStatuses] = useState<StatusOption[]>(
		statusOptions.filter((option) => option.value === RuleStatus.ACTIVE)
	)

	const initialDateRange = useMemo(() => {
		const fromParam = searchParams.get('from')
		const toParam = searchParams.get('to')

		if (fromParam && toParam) {
			return {
				from: moment(Number(fromParam)).startOf('day').toDate(),
				to: moment(Number(toParam)).endOf('day').toDate()
			}
		}

		return {
			from: moment().subtract(30, 'days').startOf('day').toDate(),
			to: moment().endOf('day').toDate()
		}
	}, [searchParams])

	const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(
		initialDateRange
	)

	// Update date range when URL params change
	useEffect(() => {
		const fromParam = searchParams.get('from')
		const toParam = searchParams.get('to')

		if (fromParam && toParam) {
			setDateRange({
				from: moment(Number(fromParam)).startOf('day').toDate(),
				to: moment(Number(toParam)).endOf('day').toDate()
			})
		}
	}, [searchParams])
	const [searchQuery, setSearchQuery] = useState('')
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
	const [selectedRules, setSelectedRules] = useState<Set<string>>(new Set())
	const [openScheduleDialog, setOpenScheduleDialog] = useState(false)
	const [chartMetric, setChartMetric] =
		useState<RuleAnalysisMetric>('executions')
	const [sortColumn, setSortColumn] = useState<{
		key: string
		direction: 'asc' | 'desc'
	}>({
		key: 'executions',
		direction: 'desc'
	})

	const statusKey = useMemo(() => {
		if (
			selectedStatuses.length === 0 ||
			selectedStatuses.length === statusOptions.length
		) {
			return undefined
		}
		return selectedStatuses
			.map((s) => s.value)
			.sort()
			.join(',')
	}, [selectedStatuses])
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery)
		}, 2000)

		return () => clearTimeout(timer)
	}, [searchQuery])

	// The Analysis table lists executions across website, ad-account and
	// campaign levels, so the edit lookup must cover every rule on the asset —
	// not just the current page's ownership. Querying by asset_id alone returns
	// rules at all levels. In demo mode the executions table is keyed to the
	// 'website-demo' asset, so the lookup must use the same id or none of the
	// rows resolve and every edit button is disabled.
	const query = useMemo(
		() => ({ asset_id: demoMode ? 'website-demo' : asset?.id }),
		[demoMode, asset?.id]
	)

	const {
		data: allRulesData,
		isLoading: isLoadingAllRules,
		refetch: refetchRules
	} = useQuery(
		// Distinct from the Protection tab's 'rules-panel' key: that query is
		// level-scoped (prepareQuery) while this one is asset-wide. Both tabs stay
		// mounted at once, so sharing a key would let them clobber each other's
		// cached data. targetId is omitted because this query no longer uses it.
		['rules-panel-analyze', asset?.account_id, asset?.id],
		() =>
			Rules.listAllRules({
				accountId: demoMode ? 'workspace-demo' : asset?.account_id,
				query
			}),
		{
			select: mapFetchedRules,
			enabled: !!asset?.id
		}
	)

	const rulesMap = useMemo(() => {
		if (!allRulesData) return new Map<string, RuleFormType>()
		return new Map(allRulesData.map((rule) => [rule.id!, rule]))
	}, [allRulesData])

	const {
		isLoading: isLoadingExecutions,
		data: executionData,
		refetch: refetchExecutions
	} = useQuery(
		[
			'rule-executions',
			asset?.account_id,
			asset?.id,
			dateRange.from.getTime(),
			dateRange.to.getTime(),
			statusKey || 'all-statuses',
			debouncedSearchQuery || '',
			sortColumn.key,
			sortColumn.direction,
			demoMode
		],
		() => {
			const queryParams: any = {
				asset_id: demoMode ? 'website-demo' : asset?.id || '',
				from: dateRange.from.getTime(),
				to: dateRange.to.getTime(),
				search: debouncedSearchQuery || undefined,
				sort:
					sortColumn.direction === 'desc'
						? `-${sortColumn.key}`
						: sortColumn.key
			}

			if (statusKey) {
				queryParams.status = statusKey
			}

			return Rules.getRuleExecutions({
				accountId: demoMode ? 'workspace-demo' : asset?.account_id,
				query: queryParams
			})
		},
		{
			enabled: !!asset?.id
		}
	)

	useEffect(() => {
		if (activeTab) {
			refetchExecutions()
		}
	}, [activeTab, refetchExecutions])

	const {
		rules: allRules,
		dailyExecutions,
		dailyBlockedSources
	} = useMemo(
		() =>
			processRulesAndExecutions(
				executionData,
				dateRange,
				assetStore.adAccounts
			),
		[
			executionData,
			dateRange.from.getTime(),
			dateRange.to.getTime(),
			assetStore.adAccounts
		]
	)

	const sortedRules = useMemo(() => allRules, [allRules])

	useEffect(() => {
		if (sortedRules.length === 0) {
			setSelectedRules(new Set())
			return
		}

		setSelectedRules((prev) => {
			const visibleRuleIds = new Set(sortedRules.map((r) => r.ruleId))
			const filtered = Array.from(prev).filter((id) => visibleRuleIds.has(id))
			return new Set(filtered.slice(0, 5))
		})
	}, [sortedRules])

	useEffect(() => {
		if (
			allRules.length > 0 &&
			selectedRules.size === 0 &&
			!isLoadingExecutions
		) {
			const activeRules = allRules.filter((r) => r.status === RuleStatus.ACTIVE)
			const rulesToSelect =
				activeRules.length >= 3
					? activeRules.slice(0, 3)
					: allRules.slice(0, Math.min(3, allRules.length))

			const top3RuleIds = rulesToSelect.map((r) => r.ruleId)
			if (top3RuleIds.length > 0) {
				setSelectedRules(new Set(top3RuleIds))
			}
		}
	}, [allRules.length, isLoadingExecutions])

	const chartColors = useMemo(
		() => [themeColors.navy, themeColors.brand, themeColors.muted, themeColors.yellow, themeColors.gray],
		[themeColors]
	)

	const ruleColorMap = useMemo(() => {
		const colorMap = new Map<string, string>()
		const selectedRulesArray = Array.from(selectedRules)
		selectedRulesArray.forEach((ruleId, index) => {
			colorMap.set(ruleId, chartColors[index % chartColors.length])
		})
		return colorMap
	}, [selectedRules, chartColors])

	const ruleMetaMap = useMemo(() => {
		const map = new Map<string, { name: string }>()
		allRules.forEach((rule) => {
			map.set(rule.ruleId, { name: rule.ruleName })
		})
		return map
	}, [allRules])

	const chartKeys = useMemo(() => Array.from(selectedRules), [selectedRules])
	const chartMetricData = useMemo(
		() =>
			chartMetric === 'executions' ? dailyExecutions : dailyBlockedSources,
		[chartMetric, dailyExecutions, dailyBlockedSources]
	)

	const chartData = useMemo(
		() =>
			selectedRules.size === 0
				? []
				: chartMetricData.map((day) => {
						const dataPoint: any = { date: day.date }
						selectedRules.forEach((ruleId) => {
							const value = day[ruleId]
							dataPoint[ruleId] = typeof value === 'number' ? value : 0
							const color = ruleColorMap.get(ruleId)
							if (color) {
								dataPoint[`color_${ruleId}`] = color
							}
						})
						return dataPoint
				  }),
		[selectedRules, chartMetricData, ruleColorMap]
	)

	const handleRuleToggle = useCallback(
		createHandleRuleToggle(selectedRules, setSelectedRules, trackPostHogEvent),
		[selectedRules, trackPostHogEvent]
	)

	const handleRefetch = useCallback(() => {
		refetchExecutions()
		refetchRules()
	}, [refetchExecutions, refetchRules])

	const tableColumns = useTableColumns({
		selectedRules,
		sortedRules,
		dateRange,
		handleRuleToggle,
		setSelectedRules,
		level,
		targetId,
		targetName,
		platform,
		refetchExecutions: handleRefetch,
		rulesMap
	})

	const handleSort = useCallback(
		(key: string) => {
			handleTableSort(key, setSortColumn, trackPostHogEvent)
		},
		[trackPostHogEvent]
	)

	const handleClearFilters = useCallback(() => {
		setSelectedStatuses(
			statusOptions.filter((option) => option.value === RuleStatus.ACTIVE)
		)
		setSearchQuery('')
		setSelectedRules(new Set())
	}, [])

	const chartTooltip = useMemo(
		() => (data: any) => {
			const dateStr = data.date as string
			const parsedDate = moment(dateStr, 'MMM D')
			if (parsedDate.isAfter(moment(), 'day')) {
				parsedDate.subtract(1, 'year')
			}
			const formattedDate = parsedDate.format('MMM D, YYYY')

			const items = chartKeys.map((key) => {
				const val = data[key] as number
				const color = data[`color_${key}`]
				const ruleName = ruleMetaMap.get(key)?.name || key
				const formattedRuleName = ruleName
					.split('_')
					.map((word) => capitalizeFirstLetter(word))
					.join(' ')

				return {
					key,
					label: formattedRuleName,
					value: val || 0,
					color
				}
			})

			return <GChartTooltip title={formattedDate} items={items} />
		},
		[chartKeys, ruleMetaMap]
	)

	const axisBottomFormat = useMemo(
		() => createAxisBottomFormat(chartData, 'date'),
		[chartData]
	)

	const hasInitialData = executionData !== undefined
	const shouldShowFullLoading =
		(isLoadingExecutions || isLoadingAllRules) && !hasInitialData

	if (shouldShowFullLoading) {
		return <GLoading />
	}

	const hasRulesInSystem = allRulesData && allRulesData.length > 0
	const hasNoFilteredResults = allRules.length === 0
	const isSearching =
		debouncedSearchQuery && debouncedSearchQuery.trim().length > 0

	if (!hasRulesInSystem && !demoMode) {
		return (
			<GEmpty
				title="Create your first custom rule"
				description="Custom protection rules allow you to deal with fraud with surgical precision."
				action={
					<RuleForm
						level={level}
						refetch={refetchExecutions}
						targetId={targetId}
						targetName={targetName}
						platform={platform}
					>
						{(open) => (
							<GButton
								size="md"
								label="Create a rule"
								labelClassName="font-medium"
								className="h-[38px] border border-t-base-blue hover:bg-transparent hover:text-t-base-blue text-t-base-blue bg-white rounded-full px-6"
								color={'buttonPrimary'}
								variant={'text'}
								onClick={() => {
									open()
									trackPostHogEvent(
										PostHogEvent.ADD_CUSTOM_RULE,
										PostHogPath.AI_AND_CUSTOMRULES,
										null
									)
								}}
							/>
						)}
					</RuleForm>
				}
			/>
		)
	}

	return (
		<div className="flex flex-col gap-6 w-full bg-white rounded-xl p-6">
			<header className="pb-4">
				<span className="font-medium text-t-color-default text-xl">
					Custom Rules Analysis
				</span>
				<span className="text-t-checkbox-unactive font-md block leading-5 mt-1">
					Track and analyze how your custom rules are protecting your account
					over time.
				</span>
			</header>
			<div className="flex items-center justify-between gap-4">
				<div className="flex items-center gap-4">
					<div className="flex-1 max-w-[300px]">
						<GInput
							type="text"
							placeholder="Search rules..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							icon={FiSearch}
							inputClassName="placeholder:font-normal"
						/>
					</div>
					<GMultiSelect<StatusOption>
						value={
							selectedStatuses.length === 0 ? statusOptions : selectedStatuses
						}
						options={statusOptions}
						onChange={(newStatuses) => {
							setSelectedStatuses(
								newStatuses.length === 0 ? statusOptions : newStatuses
							)
						}}
						renderSelected={(selected) => {
							const displaySelected =
								selected.length === 0 ? statusOptions : selected
							return (
								<span className="text-sm flex items-center gap-2">
									<FiFilter className="w-4 h-4 text-t-dark-blue" />
									{displaySelected.length === statusOptions.length
										? 'Active'
										: displaySelected.length === 1
										? displaySelected[0].label
										: `${displaySelected.length} statuses`}
								</span>
							)
						}}
						renderLabel={(option) => option.label}
						label=""
						placeholder="Active"
						buttonClassName="h-[38px] !rounded-full !border border-t-dark-blue min-w-[150px]"
						borderColorClassName="border-t-dark-blue"
						iconColorClassName="text-t-dark-blue"
						containerClassName=""
					/>
					<GDateRange
						align="right"
						onApply={(e) => {
							setDateRange({
								from: moment(e.from).startOf('day').toDate(),
								to: moment(e.to).endOf('day').toDate()
							})
						}}
						loading={isLoadingExecutions}
						calender={FiCalendar}
						iconColorClassName="text-t-dark-blue"
						borderColorClassName="border-t-dark-blue"
						size="md"
						className="[&_button]:!border [&_button]:!border-t-dark-blue [&_button]:h-[38px]"
					/>
				</div>
				<GButton
					label="Schedule Report"
					variant="contained"
					color="yellow"
					shape="circle"
					icon={FiCalendar}
					iconPlacement="left"
					className="!shadow-s-yellow"
					onClick={() => {
						trackPostHogEvent(
							PostHogEvent.SCHEDULE_REPORT,
							PostHogPath.AI_AND_CUSTOMRULES,
							null
						)
						setOpenScheduleDialog(true)
					}}
				/>
			</div>

			<div className="border border-gray-200 rounded-lg overflow-hidden">
				{hasNoFilteredResults ? (
					<div className="p-8">
						{isSearching ? (
							<GEmpty
								title="No rules found"
								description={`No rules match "${debouncedSearchQuery}". Try a different search term.`}
								action={
									<GButton
										size="lg"
										label="Clear search"
										labelClassName="font-medium"
										className="h-[38px] border-t-base-blue hover:bg-transparent text-color-text-default rounded-full"
										color={'buttonPrimary'}
										variant={'text'}
										onClick={() => {
											setSearchQuery('')
											setDebouncedSearchQuery('')
										}}
									/>
								}
							/>
						) : (
							<GEmpty
								title="No rules found"
								description="No rules match your current filters. Try adjusting your status filters."
								action={
									<GButton
										size="lg"
										label="Clear filters"
										labelClassName="font-medium"
										className="h-[38px] border-t-base-blue hover:bg-transparent text-color-text-default rounded-full"
										color={'buttonPrimary'}
										variant={'text'}
										onClick={handleClearFilters}
									/>
								}
							/>
						)}
					</div>
				) : (
					<>
						<GTable
							data={sortedRules}
							columns={tableColumns}
							sortListener={handleSort}
							sort={
								sortColumn.direction === 'desc'
									? `-${sortColumn.key}`
									: sortColumn.key
							}
							containerClassName="rounded-lg"
							showPagination={false}
							rowClassName={(rule) =>
								selectedRules.has(rule.ruleId) ? 'bg-gray-50' : 'bg-white'
							}
						/>
						{selectedRules.size >= 5 && (
							<div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
								<p className="text-sm text-blue-800">
									Maximum 5 rules can be selected.{' '}
								</p>
							</div>
						)}
					</>
				)}
			</div>

			{selectedRules.size > 0 && (
				<div className="border border-gray-200 rounded-lg p-6">
					<div className="flex flex-wrap items-center justify-between gap-4 mb-4">
						<div className="flex items-center gap-2">
							<h3 className="text-lg font-medium">
								Daily {getRuleMetricLabel(chartMetric)} by Custom Rule
							</h3>
							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
								content={
									<div className="flex flex-col">
										Executions count rule triggers. Blocked sources count the
										protective actions created from those rules.
									</div>
								}
							>
								<AiOutlineQuestionCircle className="w-4 h-4 text-t-gray-hold" />
							</GTooltip>
						</div>
						<Dropdown
							value={chartMetric}
							onValueChange={(v) =>
								setChartMetric(v as 'executions' | 'blockedSources')
							}
							variant="secondary"
							size="sm"
							options={[
								{
									value: 'executions',
									label: 'Executions'
								},
								{
									value: 'blockedSources',
									label: 'Blocked sources'
								}
							]}
						/>
					</div>
					{chartMetricData.length > 0 ? (
						<>
							<BarChart
								data={chartData}
								index="date"
								keys={chartKeys}
								stacked={true}
								layout="vertical"
								barPadding={0.3}
								hideAxisLeft={false}
								axisLeftLabel={`Number of ${getRuleMetricLabel(chartMetric)}`}
								containerClassName="h-[300px]"
								circleFill={false}
								axisBottomFormat={axisBottomFormat}
								tooltip={chartTooltip}
								borderRadius={4}
							/>
							<div className="flex flex-wrap gap-4 mt-4">
								{chartKeys.map((key) => {
									const rule = allRules.find((r) => r.ruleId === key)
									const color = ruleColorMap.get(key) || chartColors[0]
									const formattedRuleName = (rule?.ruleName || key)
										.split('_')
										.map((word) => capitalizeFirstLetter(word))
										.join(' ')
									return (
										<div key={key} className="flex items-center gap-2">
											<div
												className="w-4 h-4 rounded"
												style={{
													backgroundColor: color
												}}
											/>
											<span className="text-sm text-gray-600">
												{formattedRuleName}
											</span>
										</div>
									)
								})}
							</div>
						</>
					) : (
						<GEmpty
							title={`Looks like none of your custom rules have ${getRuleMetricLabel(
								chartMetric
							)} during the selected period.`}
							description=""
						/>
					)}
				</div>
			)}
			<ScheduleReportsDialog
				open={openScheduleDialog}
				onClose={() => setOpenScheduleDialog(false)}
				type="CUSTOM_RULES"
				preselectedRuleIds={
					selectedRules.size > 0 ? Array.from(selectedRules) : null
				}
			/>
		</div>
	)
}
